import './App.css';
import {useCallback, useEffect, useRef, useState} from "react";
import {extractFramesFromH264} from "./VideoUtils";

import {pcmHexToWavBlob} from "./AudioUtils";

// UUID generation function for compatibility
function generateUUID() {
    // Check if crypto.randomUUID is available
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    
    // Fallback implementation
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

// check window.parent
const IS_IN_IFRAME = Boolean(window.parent !== window && window.parent !== window.self);

// Remove authentication requirement, use local resources directly
let previousEmotion = "idle";

// Emotion split configuration - defines split points for emotion sequences
// Note: laugh and smile eyes remain idle (no split needed)
const emotionSplits = {
    eye: { 
        angry: 39 
        // laugh and smile eyes stay idle, no split configuration needed
    },
    mouth: { 
        angry: 10, 
        laugh: 70, 
        smile: 50 
    }
};

// Current emotion state management
let currentEmotionState = {
    eye: 'idle',
    mouth: 'idle'
};

// Reset emotion state when switching characters
function resetEmotionState() {
    currentEmotionState = {
        eye: 'idle',
        mouth: 'idle'
    };
}

// Update emotion state
function updateEmotionState(part, emotion) {
    if (currentEmotionState[part] !== emotion) {
        currentEmotionState[part] = emotion;
    }
}

// Emotion transition duration configuration (in seconds)
const EMOTION_TRANSITION_DURATION = 0.25; // Default 0.25 seconds for emotion transitions

// Get emotion sequence based on split configuration
function getEmotionSequence(part, emotion, direction, customDuration = null) {
    const animData = part === 'eye' ? configData.eye_resource[emotion] : configData.lip_resource[emotion];
    if (!animData || !animData.files) {
        reportError(`This NPC resource has no animation data for ${part}.${emotion}.`);
        return [];
    }

    const splitNum = emotionSplits[part] && emotionSplits[part][emotion] ? emotionSplits[part][emotion] : Math.floor(animData.files.length / 2);
    
    let startIndex, endIndex;
    if (direction === 'idle2emotion') {
        // First half: idle to emotion
        startIndex = 0;
        endIndex = splitNum;
    } else if (direction === 'emotion2idle') {
        // Second half: emotion to idle
        startIndex = splitNum;
        endIndex = animData.files.length;
    } else {
        // Default: full sequence
        startIndex = 0;
        endIndex = animData.files.length;
    }

    const frameCount = endIndex - startIndex;
    const totalDuration = customDuration || EMOTION_TRANSITION_DURATION;
    const frameDuration = (totalDuration * 1000) / frameCount; // Convert to milliseconds

    const sequence = [];
    for (let i = startIndex; i < endIndex; i++) {
        const file = animData.files[i];
        const key = `${animData.path}/${file}`;
        sequence.push([frameDuration, key]);
    }

    return sequence;
}

// Get emotion sequence with current transition duration setting
function getEmotionSequenceWithDuration(part, emotion, direction, transitionDuration) {
    return getEmotionSequence(part, emotion, direction, transitionDuration);
}

// Get split frame for emotion hold
function getEmotionHoldFrame(part, emotion) {
    const animData = part === 'eye' ? configData.eye_resource[emotion] : configData.lip_resource[emotion];
    if (!animData || !animData.files) {
        reportError(`This NPC resource has no animation data for ${part}.${emotion}.`);
        return null;
    }

    const splitNum = emotionSplits[part] && emotionSplits[part][emotion] ? emotionSplits[part][emotion] : Math.floor(animData.files.length / 2);
    const file = animData.files[splitNum];
    const key = `${animData.path}/${file}`;
    
    return key;
}

// Handle emotion transition with complex logic
function handleEmotionTransition(part, newEmotion, transitionDuration = null) {
    const currentEmotion = currentEmotionState[part];
    
    // If same emotion, no change needed
    if (currentEmotion === newEmotion) {
        return;
    }
    
    // Clear current animations for this part
    if (part === 'eye') {
        eyeAnimation.length = 0;
    } else if (part === 'lip') {
        lipAnimation.length = 0;
    }
    
    // Handle different transition scenarios
    if (currentEmotion === 'idle' && newEmotion !== 'idle') {
        // From idle to emotion: play idle2emotion sequence
        appendEmotionQueue(newEmotion, 'idle2emotion', [part === 'mouth' ? 'lip' : part], transitionDuration);
        
        // Set idle animation to hold the emotion at split frame
        const holdFrame = getEmotionHoldFrame(part, newEmotion);
        if (holdFrame) {
            if (part === 'eye') {
                eyeIdleAnim.length = 0;
                eyeIdleAnim.push([5000, holdFrame]); // 5 second hold
            } else if (part === 'mouth') {
                lipIdleAnim.length = 0;
                lipIdleAnim.push([10, holdFrame]); // Short hold for lip
                lipAnimationEnabled = true; // Enable lip animation for emotion hold
            }
        }
        
        // Update emotion state
        updateEmotionState(part, newEmotion);
        
    } else if (currentEmotion !== 'idle' && newEmotion === 'idle') {
        // From emotion to idle: play emotion2idle sequence
        appendEmotionQueue(currentEmotion, 'emotion2idle', [part === 'mouth' ? 'lip' : part], transitionDuration);
        
        // Set idle animation back to default
        if (part === 'eye') {
            eyeIdleAnim.length = 0;
            eyeIdleAnim.push([5000, "eye_idle"]); // Default eye idle
        } else if (part === 'mouth') {
            lipIdleAnim.length = 0;
            lipIdleAnim.push([10, "lip_idle"]); // Default lip idle
        }
        
        // Update emotion state
        updateEmotionState(part, newEmotion);
        
    } else if (currentEmotion !== 'idle' && newEmotion !== 'idle') {
        // From emotion to emotion: first to idle, then to new emotion
        // First transition: current emotion to idle
        appendEmotionQueue(currentEmotion, 'emotion2idle', [part === 'mouth' ? 'lip' : part], transitionDuration);
        
        // Set a temporary idle state
        if (part === 'eye') {
            eyeIdleAnim.length = 0;
            eyeIdleAnim.push([5000, "eye_idle"]);
        } else if (part === 'mouth') {
            lipIdleAnim.length = 0;
            lipIdleAnim.push([10, "lip_idle"]);
        }
        
        // Schedule second transition after first completes
        const firstTransitionDuration = getEmotionSequence(part, currentEmotion, 'emotion2idle').reduce((a, b) => a + b[0], 0);
        setTimeout(() => {
            // Update state to idle first
            updateEmotionState(part, 'idle');
            
            // Second transition: idle to new emotion
            appendEmotionQueue(newEmotion, 'idle2emotion', [part === 'mouth' ? 'lip' : part], transitionDuration);
            
            // Set final emotion hold
            const holdFrame = getEmotionHoldFrame(part, newEmotion);
            if (holdFrame) {
                if (part === 'eye') {
                    eyeIdleAnim.length = 0;
                    eyeIdleAnim.push([5000, holdFrame]);
                } else if (part === 'mouth') {
                    lipIdleAnim.length = 0;
                    lipIdleAnim.push([10, holdFrame]);
                    lipAnimationEnabled = true; // Enable lip animation for emotion hold
                }
            }
            
            // Update final state
            updateEmotionState(part, newEmotion);
        }, firstTransitionDuration + 100); // Small delay to ensure smooth transition
    } else {
        // For other cases (idle->emotion, emotion->idle), update state immediately
        updateEmotionState(part, newEmotion);
    }
}

async function fetchWithAuth(url, options = {}) {
    // Simplified version, send request directly without adding auth headers
    return fetch(url, {
        ...options,
    });
}

// Global AudioContext (required for Web Audio playback)
export const ctx = new (window.AudioContext || window.webkitAudioContext)();

// Function called only once to unlock audio
function unlockAudio() {
    if (ctx.state === 'suspended') ctx.resume();      // For Web Audio
    // Also try playing HTMLMediaElement slightly in advance for safety
    const probe = new Audio();
    probe.muted = true;
    probe.play().catch(() => {/* ignore */
    });

    window.removeEventListener('pointerup', unlockAudio);
}

// Unlock on first pointerup (click/touch) anywhere on the page
window.addEventListener('pointerup', unlockAudio, {once: true});

export async function speak(blob) {
    if (IS_IN_IFRAME && window.parentCallback) {
        if (window.parentCallback) {
            window.parentCallback({
                "type": "aime-speak-response",
                "blob": blob
            })
        }
    } else {
        // 1) unlockAudio() has already been executed, ctx.state === 'running'
        const buf = await blob.arrayBuffer();
        const audioBuf = await ctx.decodeAudioData(buf);

        const src = ctx.createBufferSource();
        src.buffer = audioBuf;
        src.connect(ctx.destination);
        src.start();
    }
}

let gl = null;

const programCache = {};
const textureCache = {};
const bufferCache = {};

let configData = null;
let npcData = null;

const previewZip = {};

let previewLoaded = false;

let lipIdleFrame = 0;
let eyeIdleFrame = 0;

const lipIdleAnim = [];
const lipAnimation = [];

let lipAnimationEnabled = false;

// Store emotion state before lipsync for restoration
let preLipsyncMouthEmotion = null;
let preLipsyncEyeEmotion = null;

const eyeIdleAnim = [];
const eyeAnimation = [];

// Blink control
let lastBlinkTime = 0;
const blinkInterval = 5000; // Blink every 5 seconds

let pcmTimeout = null;

// Surfaces a failure in the UI. Assigned by App() on mount; the no-op keeps the
// module-scope helpers callable before React has mounted. Everything in this file
// used to report failures to the console only, which made a broken resource or a
// failed request look identical to a working one that simply had nothing to say.
let reportError = () => {};

// initialize canvas for webgl
function initCanvas() {
    const canvas = document.getElementById("canvas");
    if (!canvas) return;

    gl = canvas.getContext("webgl");
    if (!gl) {
        console.error("WebGL not supported, falling back to experimental-webgl");
        return;
    }

    canvas.width = 1080;
    canvas.height = 1920;

    // Set the viewport to match the canvas size
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clearColor(0.0, 0.0, 0.0, 0.0); // Set clear color to black
    gl.clear(gl.COLOR_BUFFER_BIT); // Clear the color buffer
}

function loadShader(type, source) {
    const shader = gl.createShader(type);
    if (!shader) {
        console.error("Unable to create shader");
        return null;
    }
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error("An error occurred compiling the shaders: " + gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
    }

    return shader;
}

function createProgram(vertexShaderSource, fragmentShaderSource) {
    const vertexShader = loadShader(gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = loadShader(gl.FRAGMENT_SHADER, fragmentShaderSource);

    if (!vertexShader || !fragmentShader) {
        console.error("Failed to create shaders");
        return null;
    }

    const program = gl.createProgram();
    if (!program) {
        console.error("Unable to create program");
        return null;
    }

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        console.error("Unable to initialize the shader program: " + gl.getProgramInfoLog(program));
        gl.deleteProgram(program);
        return null;
    }

    return program;
}

// load image as texture
async function loadTexture(url) {
    const image = new Image();

    let src;

    if (previewZip[url]) {
        src = previewZip[url];
    } else {
        src = url;
    }

    const response = await fetch(src);
    if (!response.ok) {
        console.error("Failed to fetch image:", src, "Status:", response.status, response.statusText);
        return null;
    }
    
    const buf = await response.blob();

    if (!buf) {
        console.error("Failed to fetch image:", url);
        return null;
    }

    image.src = URL.createObjectURL(buf);

    return new Promise((resolve, reject) => {
        image.onload = () => {
            const texture = gl.createTexture();
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

            resolve(texture);
        };
        image.onerror = (err) => {
            console.error("Failed to load texture:", url);
            reject(new Error(`Failed to load texture: ${url}`));
        };
    });
}

async function loadImageWithPreview(key, path) {
    const texture = await loadTexture(path);

    if (!texture) {
        console.error("Failed to load preview texture for:", key);
        throw new Error("Failed to load preview texture");
    }

    textureCache[key] = texture;
    //
}

async function loadVideoTexture(keys, path, cropWidth = 0, cropHeight = 0) {

    const buffer = await fetch(path).then(res => res.arrayBuffer());
    const frames = await extractFramesFromH264(gl, buffer);


    if (!frames || frames.length === 0) {
        console.error("Failed to extract frames from video:", path);
        throw new Error("Failed to extract frames from video");
    }

    if (frames.length !== keys.length) {
        console.warn(`Number of frames (${frames.length}) does not match number of keys (${keys.length})`);
    }

    for (let i = 0; i < frames.length; i++) {
        const key = keys[i];
        const frame = frames[i];

        if (!frame) {
            console.error(`Frame ${i} is invalid for key: ${key}`);
            continue;
        }

        textureCache[key] = frame;
    }
}

function appendEmotionQueue(emotion, direction = 'idle2emotion', filter = ['eye', 'lip'], transitionDuration = null) {
    let totalDuration = 0;

    if (filter.includes('eye')) {
        const eyeSequence = getEmotionSequence('eye', emotion, direction, transitionDuration);
        eyeAnimation.push(...eyeSequence);
        totalDuration = Math.max(totalDuration, eyeSequence.reduce((a, b) => a + b[0], 0));
    }

    if (filter.includes('lip')) {
        const lipSequence = getEmotionSequence('lip', emotion, direction, transitionDuration);
        lipAnimation.push(...lipSequence);
        totalDuration = Math.max(totalDuration, lipSequence.reduce((a, b) => a + b[0], 0));
        
        // Enable lip animation when lip sequences are added
        if (lipSequence.length > 0) {
            lipAnimationEnabled = true;
        }
    }

    return totalDuration;
}

function setEmotion(emotion, transitionDuration = null) {
    // Handle idle emotion specially - set up blink animation
    if (emotion === "idle") {
        // Handle transitions to idle for both parts
        if (currentEmotionState.eye !== 'idle') {
            handleEmotionTransition('eye', 'idle', transitionDuration);
        }
        if (currentEmotionState.mouth !== 'idle') {
            handleEmotionTransition('mouth', 'idle', transitionDuration);
        }
        
        // Set up blink animation for eyes
        const animationData = configData.eye_resource["blink"];
        if (animationData && animationData.files) {
            eyeIdleAnim.length = 0;
            for (let i = 0; i < animationData.files.length; i++) {
                const file = animationData.files[i];
                const key = `${animationData.path}/${file}`;
                eyeIdleAnim.push([1000 / 30, key]); // 30 FPS
            }
        } else {
            reportError("This NPC resource has no eye idle animation; the eyes will not blink.");
            eyeIdleAnim.length = 0;
            eyeIdleAnim.push([5000, "eye_idle"]);
        }
        
        // Set up default lip idle
        lipIdleAnim.length = 0;
        lipIdleAnim.push([10, "lip_idle"]);
        
        return;
    }
    
    // Handle non-idle emotions with new transition logic
    // For laugh and smile, eyes should remain idle (play blink animation)
    if (emotion === 'laugh' || emotion === 'smile') {
        // Eyes stay idle for laugh/smile
        if (currentEmotionState.eye !== 'idle') {
            handleEmotionTransition('eye', 'idle', transitionDuration);
        }
        // Set up blink animation for eyes
        const animationData = configData.eye_resource["blink"];
        if (animationData && animationData.files) {
            eyeIdleAnim.length = 0;
            for (let i = 0; i < animationData.files.length; i++) {
                const file = animationData.files[i];
                const key = `${animationData.path}/${file}`;
                eyeIdleAnim.push([1000 / 30, key]); // 30 FPS
            }
        }
        // Only mouth changes to the emotion
        handleEmotionTransition('mouth', emotion, transitionDuration);
    } else {
        // Check if we're transitioning from laugh/smile to another emotion
        const currentMouthEmotion = currentEmotionState.mouth;
        const currentEyeEmotion = currentEmotionState.eye;
        
        if ((currentMouthEmotion === 'laugh' || currentMouthEmotion === 'smile') && currentEyeEmotion === 'idle') {
            // Special case: from laugh/smile to another emotion
            // First, only mouth transitions to idle, eyes stay idle
            handleEmotionTransition('mouth', 'idle', transitionDuration);
            
            // Calculate the duration of mouth transition to idle
            const mouthToIdleDuration = getEmotionSequence('lip', currentMouthEmotion, 'emotion2idle').reduce((a, b) => a + b[0], 0);
            
            // After mouth finishes transitioning to idle, both eye and mouth transition to new emotion
            setTimeout(() => {
                handleEmotionTransition('eye', emotion, transitionDuration);
                handleEmotionTransition('mouth', emotion, transitionDuration);
            }, mouthToIdleDuration + 100);
        } else {
            // Normal case: both eye and mouth change
            handleEmotionTransition('eye', emotion, transitionDuration);
            handleEmotionTransition('mouth', emotion, transitionDuration);
        }
    }
}

async function fetchMessage(message, token, endpoint) {
    const response = await fetchWithAuth(endpoint, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({message, token})
    });

    if (!response.ok) {
        throw new Error("Network response was not ok");
    }

    const data = await response.json();
    return data;
}

let prevDt = 0;
let eyeFrameDt = 0;
let lipFrameDt = 0;

function render(dt) {
    const deltaTime = dt - prevDt;
    prevDt = dt;

    if (deltaTime === 0) {
        return;
    }

    // If switching resources, pause rendering to avoid flickering
    if (window.isSwitchingResource) {
        return;
    }

    // Clear the canvas
    const canvas = document.getElementById("canvas");
    if (!canvas) return;

    gl = canvas.getContext("webgl");
    if (!gl) {
        console.error("WebGL not supported, falling back to experimental-webgl");
        return;
    }

    const base_offset = [
        configData.full_body_size[0] / 2,
        configData.full_body_size[1],
    ];

    let eyeTexture = textureCache["eye_idle"];
    let lipTexture = textureCache["lip_idle"];

    const eyeFrameTime = dt - eyeFrameDt;

    if (eyeAnimation.length > 0) {
        eyeIdleFrame = 0; // Reset idle frame when an animation is playing

        const eyeAnim = eyeAnimation[0];
        eyeTexture = textureCache[eyeAnim[1]];

        if (eyeFrameTime > eyeAnim[0]) {
            eyeFrameDt += eyeAnim[0]; // Update the frame time
            eyeAnimation.shift(); // Remove the first animation frame
        }
    } else {
        // Check if it's time to blink using performance.now() for better precision
        const currentTime = performance.now();
        const timeSinceLastBlink = currentTime - lastBlinkTime;
        
        if (timeSinceLastBlink >= blinkInterval && eyeIdleAnim.length > 1) {
            // Start blinking
            lastBlinkTime = currentTime;
            eyeIdleFrame = 0; // Start from first frame of blink animation
        }
        
        // If we're in the middle of a blink or it's time to start one
        if (eyeIdleFrame < eyeIdleAnim.length - 1 || timeSinceLastBlink < blinkInterval) {
            const eyeAnim = eyeIdleAnim[eyeIdleFrame % eyeIdleAnim.length];
            eyeTexture = textureCache[eyeAnim[1]];

            if (eyeFrameTime > eyeAnim[0]) {
                eyeIdleFrame++;
                eyeFrameDt = dt;
                
                // If we've finished the blink, stay on the last frame (idle)
                if (eyeIdleFrame >= eyeIdleAnim.length) {
                    eyeIdleFrame = eyeIdleAnim.length - 1;
                }
            }
        } else {
            // Show idle frame when not blinking
            const eyeAnim = eyeIdleAnim[eyeIdleAnim.length - 1]; // Last frame (idle)
            eyeTexture = textureCache[eyeAnim[1]];
        }
    }

    const lipFrameTime = dt - lipFrameDt;

    if (lipAnimationEnabled && lipAnimation.length > 0) {
        lipIdleFrame = 0; // Reset idle frame when an animation is playing

        const lipAnim = lipAnimation[0];
        lipTexture = textureCache[lipAnim[1]];

        if (lipFrameTime > lipAnim[0]) {
            lipFrameDt += lipAnim[0]; // Update the frame time
            lipAnimation.shift(); // Remove the first animation frame
        }
    } else {
        const lipAnim = lipIdleAnim[lipIdleFrame % lipIdleAnim.length];
        lipTexture = textureCache[lipAnim[1]];
        if (lipFrameTime > lipAnim[0]) {
            lipIdleFrame++;
            lipFrameDt = dt;
        }
    }

    const eyeWidth = configData.coordinates.upper_face.lower_right[0] - configData.coordinates.upper_face.upper_left[0];
    const eyeHeight = configData.coordinates.upper_face.lower_right[1] - configData.coordinates.upper_face.upper_left[1];
    const lipWidth = configData.coordinates.lower_face.lower_right[0] - configData.coordinates.lower_face.upper_left[0];
    const lipHeight = configData.coordinates.lower_face.lower_right[1] - configData.coordinates.lower_face.upper_left[1];

    // Set the viewport to match the canvas size
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clearColor(0.0, 0.0, 0.0, 0.0); // Set clear color to black
    gl.clear(gl.COLOR_BUFFER_BIT); // Clear the color buffer

    // // 1) Enable blending
    // gl.enable(gl.BLEND);
    //
    // // 2) Set source-alpha vs destination-alpha weights
    // //    Most common 'standard' alpha blend
    // gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    // Use the default program
    const program = programCache["default"];
    if (!program) {
        console.error("No default program found");
        return;
    }

    gl.useProgram(program);

    gl.bindBuffer(gl.ARRAY_BUFFER, bufferCache["default"]);

    const positionLocation = gl.getAttribLocation(program, "aVertexPosition");
    if (positionLocation < 0) {
        console.error("Failed to get attribute location for aVertexPosition");
        return;
    }
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    gl.uniform2f(gl.getUniformLocation(program, "uScreenSize"), canvas.width, canvas.height);

    // bind mask texture if available
    if (textureCache["mask"]) {
        const maskTexture = textureCache["mask"];
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, maskTexture);
        const uMaskSampler = gl.getUniformLocation(program, "uMaskSampler");
        if (uMaskSampler) {
            gl.uniform1i(uMaskSampler, 1); // Texture unit 1 for mask
        } else {
            console.error("Failed to get uniform location for uMaskSampler");
        }
        gl.activeTexture(gl.TEXTURE0); // Switch back to texture unit 0 for the main texture
    }

    // set mask enabled to 0
    const uMaskEnabled = gl.getUniformLocation(program, "uMaskEnabled");
    if (uMaskEnabled) {
        gl.uniform1i(uMaskEnabled, 0);
    } else {
        console.error("Failed to get uniform location for uMaskEnabled");
    }

    // enable mask
    gl.uniform1i(uMaskEnabled, 1);

    // Bind the texture if available
    {
        const texture = textureCache["body"];
        if (texture) {
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.uniform4f(gl.getUniformLocation(program, "uImageCoord"), 0, 0, canvas.width, canvas.height);
            const uSampler = gl.getUniformLocation(program, "uSampler");
            if (uSampler) {
                gl.uniform1i(uSampler, 0); // Texture unit 0
            } else {
                console.error("Failed to get uniform location for uSampler");
            }
            // Draw the rectangle
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
    }

    {
        const texture = textureCache["background_image"];
        if (texture) {
            gl.bindTexture(gl.TEXTURE_2D, texture);
            const right = base_offset[0] + configData.base_coordinates.lower_right[0];
            const bottom = base_offset[1] + configData.base_coordinates.lower_right[1];
            const left = base_offset[0] + configData.base_coordinates.upper_left[0];
            const top = base_offset[1] + configData.base_coordinates.upper_left[1];
            gl.uniform4f(gl.getUniformLocation(program, "uImageCoord"), left, top, right - left, bottom - top);
            const uSampler = gl.getUniformLocation(program, "uSampler");
            if (uSampler) {
                gl.uniform1i(uSampler, 0); // Texture unit 0
            } else {
                console.error("Failed to get uniform location for uSampler");
            }
            // Draw the rectangle
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
    }

    {
        if (lipTexture) {
            gl.bindTexture(gl.TEXTURE_2D, lipTexture);
            const left = base_offset[0] + configData.coordinates.lower_face.upper_left[0];
            const top = base_offset[1] + configData.coordinates.lower_face.upper_left[1];
            const width = lipWidth;
            const height = lipHeight;
            gl.uniform4f(gl.getUniformLocation(program, "uImageCoord"), left, top, width, height);
            const uSampler = gl.getUniformLocation(program, "uSampler");
            if (uSampler) {
                gl.uniform1i(uSampler, 0); // Texture unit 0
            } else {
                console.error("Failed to get uniform location for uSampler");
            }
            // Draw the rectangle
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
    }

    {
        if (eyeTexture) {
            gl.bindTexture(gl.TEXTURE_2D, eyeTexture);
            const left = base_offset[0] + configData.coordinates.upper_face.upper_left[0];
            const top = base_offset[1] + configData.coordinates.upper_face.upper_left[1];
            const width = eyeWidth;
            const height = eyeHeight;
            gl.uniform4f(gl.getUniformLocation(program, "uImageCoord"), left, top, width, height);
            const uSampler = gl.getUniformLocation(program, "uSampler");
            if (uSampler) {
                gl.uniform1i(uSampler, 0); // Texture unit 0
            } else {
                console.error("Failed to get uniform location for uSampler");
            }
            // Draw the rectangle
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
    }

    // Unbind the buffer
    // gl.bindBuffer(gl.ARRAY_BUFFER, null);
    // gl.bindTexture(gl.TEXTURE_2D, null);

    // Cleanup
    // gl.useProgram(null);
}

// Check if currently playing audio or video
function isCurrentlyPlaying() {
    // Check if lip animation is playing
    const isLipAnimationPlaying = lipAnimationEnabled && lipAnimation.length > 0;
    
    // Check if audio is playing (by checking if pcmTimeout is not null)
    const isAudioPlaying = pcmTimeout !== null;
    
    return isLipAnimationPlaying || isAudioPlaying;
}

function ChatBotLog({log, onEnded, flush}) {
    const [currentLog, setCurrentLog] = useState("");
    const [isPlaying, setIsPlaying] = useState(false);

    let index = useRef(0);

    // Monitor playback status changes for UI updates
    useEffect(() => {
        const interval = setInterval(() => {
            const playing = isCurrentlyPlaying();
            setIsPlaying(playing);
        }, 100); // Check playback status every 100ms

        return () => clearInterval(interval);
    }, []);

    // Effect to handle typing effect
    useEffect(() => {
        const typingInterval = setInterval(() => {
            if (flush) {
                clearInterval(typingInterval);
                setCurrentLog(log);
                onEnded();
                return;
            }

            if (index.current < log.length) {
                const c = log[index.current];

                if (c) {
                    setCurrentLog((prev) => prev + c);
                }

                onEnded();
                index.current += 1;
            } else {
                onEnded();
                clearInterval(typingInterval);
            }
        }, 50); // Adjust typing speed here

        return () => clearInterval(typingInterval);
    }, [log, onEnded, flush]);

    return (
        <div className={"msg log-bot"}>
            {currentLog}
        </div>
    )
}

function App() {
    const [isLoading, setIsLoading] = useState(true);
    const [logs, setLogs] = useState([]);
    const [errorMsg, setErrorMsg] = useState(null);
    const errorTimer = useRef(null);

    const showError = useCallback((message) => {
        console.error(message);
        setErrorMsg(String(message));
        if (errorTimer.current) clearTimeout(errorTimer.current);
        errorTimer.current = setTimeout(() => setErrorMsg(null), 12000);
    }, []);

    useEffect(() => {
        reportError = showError;
        return () => {
            reportError = () => {};
            if (errorTimer.current) clearTimeout(errorTimer.current);
        };
    }, [showError]);
    const [isActive, setIsActive] = useState(false);
    const [inputText, setInputText] = useState("");
    const [userInfo, setUserInfo] = useState(null);
    const [audioData, setAudioData] = useState(null);
    const [animQueue, setAnimQueue] = useState([]);
    const [playAnimation, setPlayAnimation] = useState(false);

    const [isIframe, setIsIframe] = useState(IS_IN_IFRAME);
    const [chatEndpoint, setChatEndpoint] = useState("/api/v1/speak");

    const [mode, setmode] = useState("chat");

    const [roudness, setRoudness] = useState(0);

    // Control panel state
    const [currentResource, setCurrentResource] = useState("");
    const [availableResources, setAvailableResources] = useState([]);
    const [expression, setExpression] = useState("idle");
    const [language, setLanguage] = useState("KOR");
    const [ttsSpeed, setTtsSpeed] = useState(1.0);
    const [ttsMode, setTtsMode] = useState("Azure");
    const [visemeMode, setVisemeMode] = useState("MFA");
    const [showControlPanel, setShowControlPanel] = useState(true);
    const [isSwitchingResource, setIsSwitchingResource] = useState(false);

    // Function to dynamically build resource URLs
    const buildResourceUrl = (filename, cacheBuster = null, resourceId = null) => {
        const baseUrl = "/resource";
        const cacheParam = cacheBuster ? `?t=${cacheBuster}` : "";
        return `${baseUrl}/${filename}${cacheParam}`;
    };

    // Get NPC resource list
    const fetchAvailableResources = async () => {
        try {
            const response = await fetch("/api/v1/npc-resources");
            if (response.ok) {
                const data = await response.json();
                setAvailableResources(data.resources || []);
                
                // Use the current resource ID returned by backend, not the frontend currentResource state
                if (data.current_resource) {
                    setCurrentResource(data.current_resource);
                } else if (data.resources && data.resources.length > 0) {
                    // If backend doesn't return current resource, use the first available resource
                    setCurrentResource(data.resources[0].id);
                }
            } else {
                showError(`Could not load the NPC list (${response.status} ${response.statusText}).`);
            }
        } catch (error) {
            showError(`Could not load the NPC list: ${error.message}`);
        }
    };

    // Handle download ZIP file
    const handleDownloadZip = async () => {
        if (!currentResource) {
            alert("Please select an NPC resource first");
            return;
        }

        try {
            const response = await fetch("/api/v1/download-zip", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    resource_id: currentResource
                })
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${currentResource}_video.zip`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                const errorData = await response.json();
                alert(`Download failed: ${errorData.message || response.statusText}`);
            }
        } catch (error) {
            console.error("Error downloading ZIP:", error);
            alert(`Download failed: ${error.message}`);
        }
    };

    useEffect(() => {
        // Load available NPC resource list and initialize WebGL
        const initializeApp = async () => {
            await fetchAvailableResources();
        };
        initializeApp();
    }, []);

    // Listen for currentResource changes and initialize WebGL
    useEffect(() => {
        if (!currentResource) {
            return;
        }

        (async () => {
            try {
                // Clean up previous state to prevent resource conflicts from repeated initialization
                if (gl) {
                    // Clean up texture cache
                    Object.keys(textureCache).forEach(key => {
                        if (textureCache[key]) {
                            gl.deleteTexture(textureCache[key]);
                            delete textureCache[key];
                        }
                    });
                    // Clean up buffer cache  
                    Object.keys(bufferCache).forEach(key => {
                        if (bufferCache[key]) {
                            gl.deleteBuffer(bufferCache[key]);
                            delete bufferCache[key];
                        }
                    });
                }

            let savedUserId = localStorage.getItem("userId");

            if (!savedUserId) {
                // Generate a new user ID if not found
                savedUserId = generateUUID();
                localStorage.setItem("userId", savedUserId);
            }

            // Initialize the canvas for WebGL
            initCanvas();

            if (!gl) {
                showError("WebGL is unavailable in this browser, so the character cannot be rendered.");
                return;
            }

            // Load textures
            const imageCacheBuster = Date.now(); // Add cache busting parameter
            window.currentCacheBuster = imageCacheBuster; // Set as global variable for setEmotion to use
            await Promise.all([
                loadImageWithPreview("body", buildResourceUrl("full_body_image.jpg", imageCacheBuster, currentResource)),
                loadImageWithPreview("background_image", buildResourceUrl("background_image.jpg", imageCacheBuster, currentResource)),
                loadImageWithPreview("mask", buildResourceUrl("full_body_mask.png", imageCacheBuster, currentResource)),
                loadImageWithPreview("eye_idle", buildResourceUrl("eye_idle.jpg", imageCacheBuster, currentResource)),
                loadImageWithPreview("lip_idle", buildResourceUrl("lip_idle.jpg", imageCacheBuster, currentResource)),
                (async () => {
                    const configJson = await fetch(buildResourceUrl("config.json", imageCacheBuster, currentResource));

                    if (!configJson.ok) {
                        showError(`Could not load config.json for this NPC (${configJson.status} ${configJson.statusText}). The character cannot be rendered.`);
                        throw new Error("Failed to fetch config.json");
                    }

                    configData = await configJson.json();
                })(),
                (async () => {
                    // Simplified NPC data for local testing
                    npcData = {
                        template: "MULTI",
                        emotion: "idle"
                    };
                })(),
            ]);

            // configData is loaded, but eye_resource and lip_resource may need to be reconstructed later

            // Load shaders and create program
            const vertexShaderSource = `
            precision mediump float;
            
            attribute vec4 aVertexPosition;
            
            varying vec2 vTextureCoord;
            varying vec2 vMaskCoord;
            
            uniform vec2 uScreenSize; // screen size
            uniform vec4 uImageCoord; // xywh coordinates for the image
            
            void main(void) {            
                // Set the position of the vertex
                gl_Position = vec4(aVertexPosition.xy, 0.0, 1.0);
                gl_Position.xy = gl_Position.xy * (uImageCoord.zw / uScreenSize);
                gl_Position.xy = gl_Position.xy + vec2(-1.0, 1.0) + vec2(1.0, -1.0) * uImageCoord.zw / uScreenSize.xy;
                gl_Position.xy = gl_Position.xy + vec2(1.0, -1.0) * uImageCoord.xy / uScreenSize.xy * 2.0;
                
                vMaskCoord = (gl_Position.xy * vec2(1, -1) + vec2(1.0, 1.0)) / 2.0; // Normalize to [0, 1]
                
                vTextureCoord = (aVertexPosition.xy * vec2(1, -1) + vec2(1.0, 1.0)) / 2.0; // Normalize to [0, 1]
            }`;
            const fragmentShaderSource = `
            precision mediump float;
            
            varying vec2 vTextureCoord;
            varying vec2 vMaskCoord;
            
            uniform sampler2D uSampler;
            uniform sampler2D uMaskSampler; // Mask texture
            
            uniform int uMaskEnabled; // 0 or 1
            
            void main(void) {
                if (uMaskEnabled == 1) {
                    vec4 maskColor = texture2D(uMaskSampler, vMaskCoord);
                    if (maskColor.r < 0.1) {
                        discard; // Discard pixels based on mask
                    }
                }
                
                // gl_FragColor = vec4(vTextureCoord.xy, 0.0, 1.0); // Simple color based on texture coordinates
                gl_FragColor = texture2D(uSampler, vTextureCoord);
                // if (gl_FragColor.a < 0.1) {
                //     discard; // Discard transparent pixels
                // }
            }`;
            const program = createProgram(vertexShaderSource, fragmentShaderSource);

            if (!program) {
                console.error("Failed to create shader program");
                return;
            }

            programCache["default"] = program;

            // Set up the vertex positions
            const vertices = new Float32Array([
                -1.0, -1.0,
                1.0, -1.0,
                -1.0, 1.0,
                1.0, 1.0,
            ]);
            const vertexBuffer = gl.createBuffer();
            if (!vertexBuffer) {
                console.error("Failed to create vertex buffer");
                return;
            }

            gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

            bufferCache["default"] = vertexBuffer;

            // Load keysJson
            const [lipKeysJson, eyeKeysJson] = await Promise.all([
                fetch(buildResourceUrl("lip.json", imageCacheBuster, currentResource)),
                fetch(buildResourceUrl("eye.json", imageCacheBuster, currentResource)),
            ]);

            // Load lip textures
            if (!lipKeysJson.ok) {
                showError(`Could not load lip.json for this NPC (${lipKeysJson.status} ${lipKeysJson.statusText}). The character cannot be rendered.`);
                return;
            }

            // Load eye textures
            if (!eyeKeysJson.ok) {
                showError(`Could not load eye.json for this NPC (${eyeKeysJson.status} ${eyeKeysJson.statusText}). The character cannot be rendered.`);
                return;
            }
            const [lipKeys, eyeKeys] = await Promise.all([
                lipKeysJson.json(),
                eyeKeysJson.json(),
            ]);

            // Reconstruct eye_resource and lip_resource from eye.json and lip.json
            function reconstructResourceData(keys, resourceType) {
                const resourceData = {};
                const grouped = {};
                
                // Group by animation type
                keys.forEach(key => {
                    const parts = key.split('/');
                    const animationType = parts[1]; // eyes/angry -> angry
                    
                    if (!grouped[animationType]) {
                        grouped[animationType] = [];
                    }
                    grouped[animationType].push(parts[2]); // filename
                });
                
                // Build resource data structure
                Object.keys(grouped).forEach(animationType => {
                    const files = grouped[animationType].sort();
                    resourceData[animationType] = {
                        path: `${resourceType}/${animationType}`,
                        type: animationType === 'blink' ? 'expression' : 
                              ['angry', 'smile', 'laugh'].includes(animationType) ? 'expression' : 'viseme',
                        num: files.length,
                        files: files
                    };
                });
                
                return resourceData;
            }

            // If configData doesn't have eye_resource or lip_resource, reconstruct from eye.json and lip.json
            if (configData && !configData.eye_resource && eyeKeys) {
                configData.eye_resource = reconstructResourceData(eyeKeys, 'eyes');
            }
            if (configData && !configData.lip_resource && lipKeys) {
                configData.lip_resource = reconstructResourceData(lipKeys, 'lip');
            }

            // Reset emotion state for new character
            resetEmotionState();
            
            // Now eye_resource and lip_resource are ready, can call setEmotion
            if (configData) {
                setEmotion("idle", EMOTION_TRANSITION_DURATION);
            } else {
                showError("This NPC resource failed to load its configuration; expressions are unavailable.");
            }

            // Load video textures for lip and eye animations
            const cacheBuster = Date.now(); // Add cache busting parameter
            await Promise.all([
                loadVideoTexture(lipKeys, buildResourceUrl("lip.h264", cacheBuster, currentResource)),
                loadVideoTexture(eyeKeys, buildResourceUrl("eye.h264", cacheBuster, currentResource)),
            ]);


            previewLoaded = true;

            setIsActive(true);
            setIsLoading(false);

            // Clear switching state when new NPC is fully loaded
            setIsSwitchingResource(false);
            window.isSwitchingResource = false;

            // Start the render loop
            const renderLoop = (dt) => {
                render(dt);
                requestAnimationFrame(renderLoop);
            };
            requestAnimationFrame(renderLoop);

            // Cleanup function to stop the render loop
            return () => {
                cancelAnimationFrame(renderLoop);
            };
            } catch (error) {
                showError(`Failed to initialise the renderer: ${error.message}`);
                // Clear switching state on error
                setIsSwitchingResource(false);
                window.isSwitchingResource = false;
                setIsLoading(false);
            }
        })();
    }, [currentResource, showError]); // Add currentResource dependency, reinitialize when resource switches

    useEffect(() => {
        scrollToBottom();
    }, [logs]);

    // Listen for expression changes and trigger setEmotion
    useEffect(() => {
        if (expression && configData) {
            setEmotion(expression, EMOTION_TRANSITION_DURATION);
        }
    }, [expression]); // Remove configData from dependencies as it's not a state variable

    async function setAnimData({text, ttsData, imageClip_list}) {
        // Save current emotion state before starting lipsync
        preLipsyncMouthEmotion = currentEmotionState.mouth;
        preLipsyncEyeEmotion = currentEmotionState.eye;
        
        // If mouth is not idle, transition to idle before starting lipsync
        if (preLipsyncMouthEmotion !== 'idle') {
            handleEmotionTransition('mouth', 'idle');
            // Wait for mouth to transition to idle before proceeding
            const transitionDuration = getEmotionSequence('lip', preLipsyncMouthEmotion, 'emotion2idle').reduce((a, b) => a + b[0], 0);
            await new Promise(resolve => setTimeout(resolve, transitionDuration + 100));
        }
        
        let vd;
        
        if (imageClip_list && imageClip_list.length > 0) {
            // Use imageClip_list data directly returned from backend
            vd = imageClip_list.map((clip, index) => {
                return [
                    clip.start_time * 1000, // Convert seconds to milliseconds
                    clip.end_time * 1000,   // Convert seconds to milliseconds
                    clip.imageClip_path,
                    clip.start_frame,
                    clip.end_frame,
                    clip.reversed || false, // Include reversed parameter
                ];
            });
        } else {
            showError("The server returned no lip-sync data for this line, so the mouth will not move.");
            vd = [];
        }

        if (!vd || vd.length === 0) {
            showError("The server returned no lip-sync data for this line, so the mouth will not move.");
            lipAnimationEnabled = false;
            lipAnimation.length = 0; // Clear previous animations
            return;
        }

        lipAnimation.length = 0; // Clear previous lip animations


        let lastEndTime = 0;
        let lastFile = "/data/lip_idle.png"; // Default idle texture
        const missingVisemes = new Set();

        for (let [startTime, endTime, clip, startFrame, endFrame, reversed] of vd) {
            const len = endTime - startTime;
            const clipData = configData.lip_resource[clip];

            if (!clipData) {
                // This NPC resource does not contain the viseme the backend asked for.
                // Hold the previous frame for the clip's duration so the audio still
                // plays in sync, instead of throwing and losing the whole utterance.
                missingVisemes.add(clip);
                lipAnimation.push([
                    Math.max(0, endTime - lastEndTime), // Duration to hold
                    lastFile
                ]);
                lastEndTime = endTime;
                continue;
            }

            if (lastEndTime < startTime) {
                // If there is a gap, add the idle frame
                if (lastFile) {
                    lipAnimation.push([
                        (startTime - lastEndTime), // Duration for the idle frame
                        lastFile // Use the last file as the idle texture
                    ]);
                }
            }

            lastEndTime = endTime;

            if (endFrame === 0) {
                endFrame = clipData.files.length - 1; // Use the last frame if endFrame is 0
            }

            const files = clipData.files.slice(startFrame, endFrame + 1);
            const timePerFrame = len / files.length;

            if (reversed) {
                // Reverse playback: iterate from end to start
                for (let i = files.length - 1; i >= 0; i--) {
                    lipAnimation.push([
                        timePerFrame, // Duration for each frame
                        `${clipData.path}/${files[i]}` // Full path to the texture
                    ]);
                    lastFile = `${clipData.path}/${files[i]}`; // Update last file
                }
            } else {
                // Forward playback: iterate from start to end
                for (let i = 0; i < files.length; i++) {
                    lipAnimation.push([
                        timePerFrame, // Duration for each frame
                        `${clipData.path}/${files[i]}` // Full path to the texture
                    ]);
                    lastFile = `${clipData.path}/${files[i]}`; // Update last file
                }
            }
        }

        if (missingVisemes.size > 0) {
            showError(
                `This NPC resource is missing ${missingVisemes.size} viseme(s): ${[...missingVisemes].join(", ")}. ` +
                `The mouth was held still for those segments — the resource is incomplete and should be regenerated.`
            );
        }

        const timeSum = lipAnimation.reduce((sum, anim) => sum + anim[0], 0);

        setAudioData(ttsData.data.audio);
    }

    async function submitMessageInternal(inputText) {
        scrollToBottom();
        setIsActive(false);
        setLogs((prevLogs) => [...prevLogs, inputText]);

        try {
            // Use TTS API directly, not dependent on chatEndpoint

            const ttsResponse = await fetch("/api/v1/speak", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    message: inputText,
                    language: language,
                    speed: ttsSpeed,
                    expression: expression,
                    tts_mode: ttsMode,
                    viseme_mode: visemeMode
                }),
            });

            if (!ttsResponse.ok) {
                // The server explains itself in `detail`; statusText alone turns
                // "message has no speakable text" into a bare "Bad Request".
                let reason = ttsResponse.statusText;
                try {
                    const body = await ttsResponse.json();
                    if (body && body.detail) reason = body.detail;
                } catch (parseError) {
                    // Keep statusText if the body is not JSON.
                }
                throw new Error(reason);
            }

            const ttsData = await ttsResponse.json();

            // Handle TTS response format
            const processedData = {
                text: inputText,
                ttsData: {
                    data: {
                        audio: ttsData.audio
                    },
                    extra_info: {
                        audio_length: ttsData.duration * 1000 // Convert to milliseconds
                    }
                },
                imageClip_list: ttsData.imageClip_list || []
            };

            const response = {
                message: ttsData.message,
                animData: [processedData]
            };

            // Response processed

            const queue = response.animData;

            if (window.parentCallback) {
                window.parentCallback({
                    "type": "aime-chat-response",
                    "message": response.message,
                });
            }

            setAnimQueue(queue);

            (async () => {
                await queue[0];

                if (previousEmotion !== "idle") {
                    lipIdleAnim.length = 0;
                    lipIdleAnim.push([
                        10,
                        "/data/lip_idle.png" // Default idle texture
                    ]);

                    const time = appendEmotionQueue(previousEmotion, 'emotion2idle', ['lip']);
                    lipAnimationEnabled = true;

                    await new Promise(resolve => setTimeout(resolve, time));
                }

                // setLogs((prevLogs) => [...prevLogs, response.message]);
                setPlayAnimation(true);
                setIsActive(true);
            })();
        } catch (error) {
            showError(`Speech request failed: ${error.message}`);
            // setLogs((prevLogs) => [...prevLogs, `Error fetching response: ${error.message}`]);
            setIsActive(true);
        }
    }

    function scrollToBottom() {
        // scroll to the bottom of the chat logs whenever logs change
        const logsContainer = document.querySelector(".logs");
        if (logsContainer) {
            logsContainer.scrollTop = logsContainer.scrollHeight;
        }
    }

    async function submitMessage() {
        if (inputText.trim() === "") return;
        if (!isActive) return;
        
        // Check if currently playing, if so block new requests
        if (isCurrentlyPlaying()) {
            console.log("Currently playing, cannot send new TTS request");
            return;
        }

        setInputText("");

        if (inputText === "update") {
            const prevTemplate = npcData.template;

            let nextTemplate;

            if (prevTemplate === "man") {
                nextTemplate = "woman";
            } else {
                nextTemplate = "man";
            }

            const r = await fetchWithAuth("/api/v1/npc/switch", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    "npc": {
                        "template": nextTemplate,
                        "emotion": "idle"
                    },
                }),
            });

            const data = await r.json();

            // change token in query string
            const url = new URL(window.location.href);
            url.searchParams.set("token", data.token);
            window.location.href = url.toString();
        }

        if (["idle", "angry", "smile", "laugh"].includes(inputText.toLowerCase())) {
            const emotion = inputText.toLowerCase();

            if (previousEmotion !== emotion) {
                setEmotion(previousEmotion, EMOTION_TRANSITION_DURATION);
            }

            setEmotion(emotion, EMOTION_TRANSITION_DURATION);
            previousEmotion = emotion;

            setInputText("");
            return;
        }

        await submitMessageInternal(inputText);
    }

    function onInputButton() {
        unlockAudio();
        switch (mode) {
            case "recording":
                setmode("pause");
                break;
            case "chat":
                setmode("recording");
                break;
            case "pause":
                setmode("chat");
                break;
        }
    }

    if (window.childCallbacks) {
        window.childCallbacks.chat = ({message}) => {
            submitMessageInternal(message);
        }
        window.childCallbacks.updateRoom = async ({data}) => {
            if (data.npc.template === npcData.template) {
                const emotion = data.npc.emotion;

                if (previousEmotion !== emotion) {
                    setEmotion(previousEmotion, EMOTION_TRANSITION_DURATION);
                }

                setEmotion(emotion, EMOTION_TRANSITION_DURATION);
                previousEmotion = emotion;

                return;
            }

            const r = await fetchWithAuth("/api/v1/npc/switch", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(data),
            });

            const response = await r.json();

            // change token in query string
            const url = new URL(window.location.href);
            url.searchParams.set("token", response.token);
            window.location.href = url.toString();
        }
        window.childCallbacks.setEndpoint = (endpoint) => {
            setChatEndpoint(endpoint);
        }
    }

    useEffect(() => {
        if (playAnimation) {
            setPlayAnimation(false);
        } else {
            return; // Do nothing if not playing animation
        }

        if (animQueue.length === 0) {

            if (previousEmotion !== "idle") {

                const lipAnimation = configData.lip_resource[previousEmotion];

                const mid = Math.floor(lipAnimation.files.length / 2);
                const midFile = lipAnimation.files[mid];
                const midKey = `${lipAnimation.path}/${midFile}`;

                lipIdleAnim.length = 0;
                lipIdleAnim.push([
                    10,
                    midKey // Middle frame as idle
                ]);

                appendEmotionQueue(previousEmotion, 'idle2emotion', ['lip']);
                lipAnimationEnabled = true;
            }

            return;
        }

        (async () => {
            const animData = await animQueue[0];
            try {
                await setAnimData(animData);
            } catch (error) {
                // Without this the rejection becomes an unhandled promise rejection and
                // the utterance dies silently, with no feedback anywhere in the UI.
                showError(`Failed to build the lip animation: ${error.message}`);
            }
            setAnimQueue(animQueue.slice(1));
        })();
    }, [animQueue, playAnimation, showError]);

    useEffect(() => {
        if (!audioData) return;

        const blob = pcmHexToWavBlob(audioData);
        setAudioData(null);

        (async () => {
            await speak(blob);

            lipAnimationEnabled = true;
            lipFrameDt = prevDt;

            const hexLen = audioData.length;   // 예: 96 000글자(hex)
            const sampleRate = 44100;  // Hz
            const channels = 1;  // 모노
            const bytesPerSample = 2;  // 16-bit PCM

            const bytes = hexLen / 2;                           // 48 000 byte
            const frames = bytes / (bytesPerSample * channels);  // 24 000 frame
            const seconds = frames / sampleRate;                  // 0.5 초

            clearTimeout(pcmTimeout);

            pcmTimeout = setTimeout(() => {
                lipAnimationEnabled = false;
                lipAnimation.length = 0; // Clear remaining lip animations
                
                // Restore emotion state after lipsync is complete
                // Directly update emotion state without triggering animations to avoid blocking input
                if (preLipsyncMouthEmotion && preLipsyncMouthEmotion !== 'idle') {
                    updateEmotionState('mouth', preLipsyncMouthEmotion);
                    // Set the emotion hold frame directly without animation
                    const holdFrame = getEmotionHoldFrame('mouth', preLipsyncMouthEmotion);
                    if (holdFrame) {
                        lipIdleAnim.length = 0;
                        lipIdleAnim.push([10, holdFrame]);
                        lipAnimationEnabled = true;
                    }
                }
                if (preLipsyncEyeEmotion && preLipsyncEyeEmotion !== 'idle') {
                    updateEmotionState('eye', preLipsyncEyeEmotion);
                    // Set the emotion hold frame directly without animation
                    const holdFrame = getEmotionHoldFrame('eye', preLipsyncEyeEmotion);
                    if (holdFrame) {
                        eyeIdleAnim.length = 0;
                        eyeIdleAnim.push([5000, holdFrame]);
                    }
                }
                
                // Update expression dropdown to match the restored emotion state
                if (preLipsyncMouthEmotion && preLipsyncMouthEmotion !== 'idle') {
                    setExpression(preLipsyncMouthEmotion);
                } else if (preLipsyncEyeEmotion && preLipsyncEyeEmotion !== 'idle') {
                    setExpression(preLipsyncEyeEmotion);
                } else {
                    setExpression('idle');
                }
                
                // Clear the saved states
                preLipsyncMouthEmotion = null;
                preLipsyncEyeEmotion = null;
                
                // Clear the timeout reference to indicate audio playback is complete
                pcmTimeout = null;
                
                setPlayAnimation(true);
            }, (seconds + 0.1) * 1000);
        })();
    }, [audioData]);

    return (
        <div className="wrapper" onContextMenu={(e) => {
            e.preventDefault()
        }}>
            {errorMsg && (
                <div
                    onClick={() => setErrorMsg(null)}
                    title="Click to dismiss"
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2000,
                        background: '#c0392b', color: 'white', cursor: 'pointer',
                        padding: '10px 16px', fontSize: '14px', lineHeight: 1.45,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                        display: 'flex', alignItems: 'flex-start', gap: '10px'
                    }}
                >
                    <span style={{flexShrink: 0}}>⚠️</span>
                    <span style={{flex: 1, wordBreak: 'break-word'}}>{errorMsg}</span>
                    <span style={{flexShrink: 0, opacity: 0.75}}>✕</span>
                </div>
            )}
            {/* Control Panel Toggle Button - Always visible */}
            <div className="control-panel-toggle" style={{position: 'fixed', top: '10px', right: '10px', zIndex: 1000}}>
                <button 
                    className="toggle-btn" 
                    onClick={() => setShowControlPanel(!showControlPanel)}
                    style={{
                        backgroundColor: showControlPanel ? '#dc3545' : '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                    }}
                >
                    {showControlPanel ? "Hide Panel" : "Show Panel"}
                </button>
            </div>

            {/* Control Panel */}
            <div className="control-panel" style={{display: showControlPanel ? "block" : "none"}}>
                <div className="control-header">
                    <h3>Control Panel</h3>
                    <button 
                        className="home-btn" 
                        onClick={() => window.location.href = 'http://192.168.0.12:1246/'}
                        style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            marginLeft: '10px'
                        }}
                    >
                        Back to Home
                    </button>
                </div>
                
                <div className="control-section">
                    <label>NPC Character:</label>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '5px' }}>
                        Available: {availableResources.length} NPCs
                        {isSwitchingResource && (
                            <span style={{ color: '#007bff', marginLeft: '10px' }}>
                                🔄 Switching character...
                            </span>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <select 
                            value={currentResource} 
                            onChange={async (e) => {
                                const newResourceId = e.target.value;
                                
                                // Prevent duplicate switching
                                if (newResourceId === currentResource) {
                                    return;
                                }
                                
                                // Set switching state first to prevent displaying incorrect content during switching
                                setIsSwitchingResource(true);
                                setIsLoading(true);
                                window.isSwitchingResource = true;
                                
                                try {
                                    const response = await fetch("/api/v1/switch-resource", {
                                        method: "POST",
                                        headers: {
                                            "Content-Type": "application/json"
                                        },
                                        body: JSON.stringify({
                                            resource_id: newResourceId
                                        })
                                    });
                                    
                                    if (response.ok) {
                                        // Wait for backend processing to complete
                                        await new Promise(resolve => setTimeout(resolve, 100));
                                        // Reset emotion state when switching characters
                                        resetEmotionState();
                                        // Reset control panel expression to idle
                                        setExpression("idle");
                                        // Switch to new resource without page reload
                                        setCurrentResource(newResourceId);
                                    } else {
                                        showError(`Could not switch NPC (${response.status} ${response.statusText}).`);
                                        const result = await response.json();
                                        console.error("Error details:", result);
                                        // If switching fails, restore state
                                        setIsSwitchingResource(false);
                                        setIsLoading(false);
                                        window.isSwitchingResource = false;
                                    }
                                } catch (error) {
                                    showError(`Could not switch NPC: ${error.message}`);
                                    // If switching fails, restore state
                                    setIsSwitchingResource(false);
                                    setIsLoading(false);
                                    window.isSwitchingResource = false;
                                }
                            }}
                            className="control-input"
                            style={{ minWidth: '250px' }}
                        >
                            {availableResources.length === 0 || !currentResource ? (
                                <option value="">Loading NPCs...</option>
                            ) : (
                                availableResources.map((resource) => (
                                    <option key={resource.id} value={resource.id}>
                                        {resource.name || resource.id}
                                    </option>
                                ))
                            )}
                        </select>
                        <button 
                            onClick={fetchAvailableResources}
                            className="control-button"
                            style={{ 
                                backgroundColor: '#007bff', 
                                color: 'white', 
                                border: 'none',
                                padding: '10px 20px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                marginRight: '10px'
                            }}
                        >
                            Refresh NPC
                        </button>
                        <button 
                            onClick={handleDownloadZip}
                            className="control-button"
                            style={{ 
                                backgroundColor: '#28a745', 
                                color: 'white', 
                                border: 'none',
                                padding: '10px 20px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Download ZIP
                        </button>
                    </div>
                </div>

                <div className="control-section">
                    <label>Expression:</label>
                    <select 
                        value={expression} 
                        onChange={(e) => setExpression(e.target.value)}
                        className="control-select"
                        disabled={isCurrentlyPlaying()}
                        style={{
                            opacity: isCurrentlyPlaying() ? 0.6 : 1,
                            cursor: isCurrentlyPlaying() ? 'not-allowed' : 'default'
                        }}
                    >
                        <option value="idle">idle</option>
                        <option value="angry">angry</option>
                        <option value="laugh">laugh</option>
                        <option value="smile">smile</option>
                    </select>
                </div>

                <div className="control-section">
                    <label>Language:</label>
                    <select 
                        value={language} 
                        onChange={(e) => setLanguage(e.target.value)}
                        className="control-select"
                    >
                        <option value="JPN">jpn</option>
                        <option value="ENG">eng</option>
                        <option value="KOR">kor</option>
                    </select>
                </div>

                <div className="control-section">
                    <label>TTS Speed:</label>
                    <input 
                        type="number" 
                        min="0.5" 
                        max="2.0" 
                        step="0.1" 
                        value={ttsSpeed} 
                        onChange={(e) => setTtsSpeed(parseFloat(e.target.value))}
                        className="control-input"
                    />
                </div>

                <div className="control-section">
                    <label>TTS Mode:</label>
                    <select 
                        value={ttsMode} 
                        onChange={(e) => setTtsMode(e.target.value)}
                        className="control-select"
                    >
                        <option value="Azure">Azure</option>
                        <option value="Minimax">Minimax</option>
                    </select>
                </div>

                <div className="control-section">
                    <label>Viseme Mode:</label>
                    <select 
                        value={visemeMode} 
                        onChange={(e) => setVisemeMode(e.target.value)}
                        className="control-select"
                    >
                        <option value="MFA">MFA</option>
                        <option value="Azure">Azure</option>
                    </select>
                </div>
            </div>

            <div className="main-content">
                <div className="screen">
                    <canvas id="canvas"/>
                    {/* Loading overlay for NPC switching */}
                    {isSwitchingResource && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 1000,
                            color: 'white',
                            fontSize: '24px',
                            fontWeight: 'bold'
                        }}>
                            <div style={{ marginBottom: '20px' }}>Loading New NPC...</div>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                border: '4px solid #ffffff',
                                borderTop: '4px solid transparent',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }}></div>
                        </div>
                    )}
                    <div className={"chat"} style={{display: isIframe ? "none" : "block"}}>
                        <div className={"logs-container"}>
                            <div className={"logs"}>
                                <div className={"logs-top"}/>
                                {
                                    logs.map((log, index) => {
                                        // All current logs are user inputs, display on the right
                                        return <div key={index} className={"msg log-user"}>{log}</div>;
                                    })
                                }
                            </div>
                        </div>
                        <div className={"input-area"}>
                            {isCurrentlyPlaying() && (
                                <div style={{
                                    color: '#ff6b6b',
                                    fontSize: '12px',
                                    marginBottom: '5px',
                                    textAlign: 'center'
                                }}>
                                    🎵 Currently playing...
                                </div>
                            )}
                            <input id={"input-text"} type={"text"} value={inputText}
                                   placeholder={isCurrentlyPlaying() ? "Currently playing, please wait..." : "Please enter the text here..."}
                                   disabled={isCurrentlyPlaying()}
                                   style={{
                                       opacity: isCurrentlyPlaying() ? 0.6 : 1,
                                       cursor: isCurrentlyPlaying() ? 'not-allowed' : 'text'
                                   }}
                                   onChange={(e) => {
                                       setInputText(e.target.value);
                                   }} onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    unlockAudio();
                                    e.preventDefault();
                                    
                                    // Check if currently playing, if so block new requests
                                    if (isCurrentlyPlaying()) {
                                        console.log("Currently playing, cannot send new TTS request");
                                        return;
                                    }
                                    
                                    submitMessage();
                                }
                            }}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default App;
